import { AlertTriangle, RefreshCw, SearchX } from 'lucide-react';
import { Button } from './Button';
import { Card } from './Card';
import { cn } from '@/lib/cn';

/**
 * Tiga keadaan yang menentukan apakah sebuah antarmuka terasa matang atau
 * setengah jadi: kosong, memuat, dan gagal. Rancangan awal hanya menggambarkan
 * keadaan "data ada" - padahal pengguna baru menghabiskan menit-menit pertama
 * mereka justru di keadaan kosong.
 */

/** Keadaan kosong: selalu menawarkan langkah berikutnya, bukan sekadar "tidak ada data". */
export const EmptyState = ({
  icon: Icon = SearchX,
  title,
  description,
  action,
  secondaryAction,
  compact = false,
  className,
}) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center text-center',
      compact ? 'px-6 py-10' : 'px-6 py-16',
      className,
    )}
  >
    <div className="grid size-12 place-items-center rounded-xl bg-[var(--surface-sunken)] text-[var(--text-muted)]">
      <Icon className="size-6" aria-hidden="true" />
    </div>
    <h3 className="mt-4 text-[15px] font-bold text-[var(--text-primary)]">{title}</h3>
    {description && (
      <p className="mt-1.5 max-w-sm text-sm leading-relaxed text-[var(--text-muted)]">
        {description}
      </p>
    )}
    {(action || secondaryAction) && (
      <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
        {action}
        {secondaryAction}
      </div>
    )}
  </div>
);

/**
 * Keadaan galat. Pesan teknis disembunyikan di balik `<details>` - pengguna
 * biasa melihat kalimat yang bisa dimengerti, sementara yang butuh detail
 * (dan tim dukungan) tetap bisa membukanya berikut ID permintaan.
 */
export const ErrorState = ({
  title = 'Gagal memuat data',
  message,
  requestId,
  onRetry,
  compact = false,
  className,
}) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center text-center',
      compact ? 'px-6 py-10' : 'px-6 py-16',
      className,
    )}
    role="alert"
  >
    <div className="grid size-12 place-items-center rounded-xl bg-danger-50 text-danger-600 dark:bg-danger-500/15 dark:text-danger-500">
      <AlertTriangle className="size-6" aria-hidden="true" />
    </div>
    <h3 className="mt-4 text-[15px] font-bold text-[var(--text-primary)]">{title}</h3>
    <p className="mt-1.5 max-w-sm text-sm leading-relaxed text-[var(--text-muted)]">
      {message ?? 'Terjadi kesalahan saat menghubungi server. Coba muat ulang halaman ini.'}
    </p>

    {onRetry && (
      <Button variant="secondary" size="sm" icon={RefreshCw} onClick={onRetry} className="mt-5">
        Coba lagi
      </Button>
    )}

    {requestId && (
      <details className="mt-4 text-left">
        <summary className="cursor-pointer text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)]">
          Detail teknis
        </summary>
        <code className="mt-2 block rounded-md bg-[var(--surface-sunken)] px-2.5 py-1.5 font-mono text-[11px] text-[var(--text-muted)]">
          ID permintaan: {requestId}
        </code>
      </details>
    )}
  </div>
);

/** Blok abu berdenyut sebagai penanda konten yang sedang dimuat. */
export const Skeleton = ({ className, ...props }) => (
  <div
    className={cn(
      'relative overflow-hidden rounded-md bg-[var(--surface-sunken)] shimmer',
      className,
    )}
    aria-hidden="true"
    {...props}
  />
);

/**
 * Kerangka tabel yang meniru bentuk akhirnya. Ini penting: kerangka yang
 * seukuran konten sungguhan mencegah tata letak melompat saat data tiba.
 */
export const TableSkeleton = ({ rows = 6, columns = 5 }) => (
  <div className="divide-y divide-[var(--border-subtle)]" aria-busy="true" aria-live="polite">
    <span className="sr-only">Memuat data…</span>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={rowIndex} className="flex items-center gap-4 px-5 py-4">
        {Array.from({ length: columns }).map((__, colIndex) => (
          <Skeleton
            key={colIndex}
            className={cn('h-4', colIndex === 0 ? 'flex-[2]' : 'flex-1')}
            style={{ opacity: 1 - rowIndex * 0.1 }}
          />
        ))}
      </div>
    ))}
  </div>
);

export const CardSkeleton = ({ count = 4 }) => (
  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-busy="true">
    {Array.from({ length: count }).map((_, i) => (
      <Card key={i} className="p-4">
        <Skeleton className="size-9 rounded-lg" />
        <Skeleton className="mt-3 h-7 w-20" />
        <Skeleton className="mt-2 h-3 w-24" />
      </Card>
    ))}
  </div>
);

/** Pemuatan seluruh halaman, dipakai saat memverifikasi sesi. */
export const PageLoader = ({ label = 'Memuat…' }) => (
  <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3">
    <div className="size-8 animate-spin rounded-full border-2 border-[var(--border-default)] border-t-accent-600" />
    <p className="text-sm text-[var(--text-muted)]">{label}</p>
  </div>
);
